import groupBy from 'object.groupby';
import React from 'react';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Binance from '../assets/exchange/binance.svg';
import Bybit from '../assets/exchange/bybit.svg';
import { uiHelpers } from '../helpers';
import { holdPlace } from '../helpers/general.helper';
import uiHelper, { bsTiny } from '../helpers/ui.helper';
import { useFavStocks } from '../hooks/useFavoriteStocks';
import { FavStock } from '../py-generated/datalouder/favorite_stock/types';
import { Category } from '../py-generated/datalouder/stock/types';

interface Props {
  onSelect: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    stock: FavStock
  ) => void;
  showHKOnly?: boolean;
  hideIndex?: boolean;
}

const sortFavStock = (a: FavStock, b: FavStock) => {
  return Number(a.unlike) - Number(b.unlike);
};

const buttonProps = {
  variant: 'outline-secondary',
  size: bsTiny,
  style: { margin: '0.4rem' },
};
const Style = styled.div`
  .like-button {
    background: linear-gradient(225deg, #00cc00 5px, #ffffff 0);
    color: #6c757d;
  }
  .like-button:hover {
    background: linear-gradient(225deg, #00cc00 5px, #6c757d 0);
    color: white;
  }
  .unlike-button {
    background: linear-gradient(225deg, #ff0000 5px, #ffffff 0);
    color: #6c757d;
  }
  .unlike-button:hover {
    background: linear-gradient(225deg, #ff0000 5px, #6c757d 0);
    color: white;
  }
  .exchange-icon {
    height: 0.7rem;
  }
`;

/**
 * Combining bybit and binance
 * @param category
 * @returns
 */
const categoryToSection = (category: Category): string => {
  switch (category) {
    case 'CRPT':
    case 'CRPTBN': {
      return '3';
    }
    case 'HK': {
      return '1';
    }
    case 'US': {
      return '2';
    }
  }
};

const FavStockButtonList: React.FC<Props> = ({
  showHKOnly,
  hideIndex,
  onSelect,
}) => {
  const { stocks: allFavStocks, loading } = useFavStocks();

  let _stocks = allFavStocks || [];
  if (hideIndex) {
    _stocks = _stocks.filter(item => !item.ticker.startsWith('^'));
  }
  if (showHKOnly) {
    _stocks = _stocks.filter(item => item.category === 'HK');
  }

  const stocksBySection = groupBy(_stocks, stock =>
    categoryToSection(stock.category)
  );

  const { t, i18n } = useTranslation();

  const renderStockButton = (stock: FavStock) => {
    return (
      <Button
        className={stock.unlike ? 'unlike-button' : 'like-button'}
        key={`${stock.category}#${stock.id}`}
        onClick={e => onSelect(e, stock)}
        title={t('added-fav-stock-at', {
          deltaTime: uiHelper.formatDeltaDateTime(
            i18n,
            new Date(stock.ts * 1000)
          ),
        })}
        {...buttonProps}
      >
        {stock.category === 'CRPTBN' && (
          <span>
            <img className="exchange-icon" src={Binance} />{' '}
          </span>
        )}
        {stock.category === 'CRPT' && (
          <span>
            <img className="exchange-icon" src={Bybit} />{' '}
          </span>
        )}
        <b>{stock.ticker}</b>{' '}
        {stock.category === 'US'
          ? uiHelpers.tradChify(stock.name[i18n.language])
          : stock.name[i18n.language]}
      </Button>
    );
  };

  return (
    <>
      <Style>
        {loading &&
          Array.from({ length: 6 }).map((_, i) => (
            <Button key={i} {...buttonProps}>
              {holdPlace()}
            </Button>
          ))}
        {Object.keys(stocksBySection)
          .toSorted()
          .map(section => (
            <div key={section} style={{ marginTop: 10, marginBottom: 10 }}>
              {stocksBySection[section]
                .sort(sortFavStock)
                .map(renderStockButton)}
            </div>
          ))}
      </Style>
    </>
  );
};

export default FavStockButtonList;
